import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Drawer, Segmented } from '../components/data';
import { Gauge } from '../components/Gauge';
import {
  DeleteButton,
  Empty,
  ErrorBanner,
  Field,
  Loading,
  Modal,
  PageHead,
} from '../components/ui';
import { api } from '../lib/api';
import { formatDate, todayLocal, weekRange } from '../lib/format';
import { useHeldResource, useResource } from '../lib/hooks';
import type { ServiceDelivery, ServicePeriod } from '../lib/types';
import { Tabs } from './am/amUi';
import { ServiceDeskScorecard } from './ServiceDeskScorecard';
import { ServiceQueues } from './ServiceQueues';

/**
 * Service Delivery.
 *
 * Six gauges and the book the desk works from. The numbers are entered rather
 * than derived — the PSA owns the tickets, and a half-integration that
 * silently disagrees with it would be worse than typing six figures once a
 * month. The shape of the page does not change when there is an API to read.
 */
export function ServiceDeliveryPage() {
  const resource = useResource<ServiceDelivery>('/service');
  /* Three views: the dials, the Service Desk items in the scorecard library,
     and every Autotask queue with its open tickets. */
  const [view, setView] = useState<'scorecard' | 'items' | 'queues'>('scorecard');
  const [editing, setEditing] = useState<ServicePeriod | 'new' | null>(null);
  const [editingTargets, setEditingTargets] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [csatRange, setCsatRange] = useState<CsatRange>('today');
  /* Each live dial keeps its own queue choice, under its own key: the
     questions they answer get asked of different desks. See useStoredQueues. */
  const [csatQueues, setCsatQueues] = useCsatQueues();
  const csat = useCsat(csatRange, csatQueues);
  const [openQueues, setOpenQueues] = useOpenQueues();
  /* How old is old, for the age dial. Remembered like the queue choices:
     a desk that watches a five-day backlog should not have to say so again
     every morning. */
  const [openAgeDays, setOpenAgeDays] = useOpenAgeDays();
  /* The SLA dials and the reopen rate, live from Autotask, over a chosen period. */
  const [slaRange, setSlaRange] = useState<CsatRange>('month');
  const [slaQueues, setSlaQueues] = useSlaQueues();
  const sla = useSla(slaRange, slaQueues);
  /* The kill ratio counts its own choice of queues, kept apart from the SLA
     dials' on purpose: the question "are we closing what arrives" is often
     asked of one desk while the clocks are watched across all of them. The
     options come from the SLA read, which already lists every queue Autotask
     has, so the picker costs no extra call. */
  const [killQueues, setKillQueues] = useKillQueues();
  /* The billable dial, live from Autotask on the Billable Labor report's own
     rule: whose time, and which week. */
  const [billableWeek, setBillableWeek] = useState<BillableWeek>('current');
  const [billablePerson, setBillablePerson] = useBillablePerson();
  const billable = useBillable(billableWeek);

  if (resource.loading) return <Loading label="Loading the scorecard" />;
  if (resource.error) return <ErrorBanner message={resource.error} />;

  const data = resource.data;
  if (!data) return null;

  const periods = data.periods ?? [];
  const current = periods[0] ?? null;
  const t = data.targets;

  return (
    <>
      <PageHead
        title="Service Delivery"
        lede="How the desk is performing, against the bar we set. Are clients happy, is time being sold, are we hitting the three SLA clocks, are we closing more than we open, and how much of the open queue is heavy or ageing — CSAT, billable share, the kill ratios and the open-ticket dials show their last Autotask reading, and each reads Autotask again only when you press its own Refresh. Billable is one engineer's or the whole desk's week, on the same rule as Reports › Billable Labor."
        actions={
          <>
            <button type="button" className="btn btn-ghost" onClick={() => setEditingTargets(true)}>
              Targets
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
              Record a month
            </button>
          </>
        }
      />

      {error && <ErrorBanner message={error} />}

      <Tabs<'scorecard' | 'items' | 'queues'>
        tabs={[
          { key: 'scorecard', label: 'Dials' },
          { key: 'items', label: 'Scorecard' },
          { key: 'queues', label: 'Queues' },
        ]}
        active={view}
        onChange={setView}
      />

      {view === 'items' && <ServiceDeskScorecard />}
      {view === 'queues' && <ServiceQueues />}

      {view === 'scorecard' && (
        <>
      {current === null && (
        <div className="gauge-grid mb-4">
          <LiveCsat
            csat={csat}
            range={csatRange}
            onRange={setCsatRange}
            queues={csatQueues}
            onQueues={setCsatQueues}
            target={t?.CsatTarget ?? null}
          />
          <LiveBillable
            billable={billable}
            week={billableWeek}
            onWeek={setBillableWeek}
            person={billablePerson}
            onPerson={setBillablePerson}
            target={t?.BillableTarget ?? null}
          />
          <LiveSla sla={sla} range={slaRange} onRange={setSlaRange} queues={slaQueues} onQueues={setSlaQueues} targets={t} />
          <LiveKillRatio
            target={t?.KillRatioTarget ?? 1}
            queues={killQueues}
            queueOptions={sla.data?.queues ?? []}
            onQueues={setKillQueues}
          />
          <LiveKillRatio target={t?.KillRatioTarget ?? 1} period="previousWeek" queues={killQueues} />
          <OpenTicketHealth
            queues={openQueues}
            onQueues={setOpenQueues}
            ageDays={openAgeDays}
            onAgeDays={setOpenAgeDays}
          />
        </div>
      )}
      {current === null && <SurveyList csat={csat} range={csatRange} />}

      {current === null ? (
        <Empty title="No numbers yet">
          <p>
            Record a month and the gauges light up. Six figures from the PSA: satisfaction,
            billable share, the three SLA attainment percentages, and tickets opened and closed.
          </p>
          <button type="button" className="btn btn-primary mt-4" onClick={() => setEditing('new')}>
            Record the first month
          </button>
        </Empty>
      ) : (
        <>
          <div className="row-between wrap mb-4">
            <div>
              <span className="eyebrow">Reporting</span>
              <h2>{current.PeriodLabel}</h2>
            </div>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => setEditing(current)}
            >
              Edit this month
            </button>
          </div>

          <div className="gauge-grid mb-4">
            {/* The only one of the six with its own evidence in this app: the
                CSAT module holds the individual responses, so the dial opens
                the ones that fall inside this month. The other five come from
                the PSA and have nothing here to drill into. */}
            {/* Today's, live from Autotask's ticket survey — not the month. */}
            <LiveCsat
              csat={csat}
              range={csatRange}
              onRange={setCsatRange}
              queues={csatQueues}
              onQueues={setCsatQueues}
              target={t?.CsatTarget ?? null}
            />
            {/* Live from Autotask on the Billable Labor report's own rule,
                for one engineer or the desk, over a week — not the month
                recorded above, which is still in the trend table. */}
            <LiveBillable
              billable={billable}
              week={billableWeek}
              onWeek={setBillableWeek}
              person={billablePerson}
              onPerson={setBillablePerson}
              target={t?.BillableTarget ?? null}
            />
            {/* Live from Autotask's ticket SLAs, over the chosen period — not the month. */}
            <LiveSla sla={sla} range={slaRange} onRange={setSlaRange} queues={slaQueues} onQueues={setSlaQueues} targets={t} />
            {/* Today's, live from Autotask — not the month recorded above. */}
            <LiveKillRatio
              target={t?.KillRatioTarget ?? 1}
              queues={killQueues}
              queueOptions={sla.data?.queues ?? []}
              onQueues={setKillQueues}
            />
            <LiveKillRatio target={t?.KillRatioTarget ?? 1} period="previousWeek" queues={killQueues} />
            <OpenTicketHealth
              queues={openQueues}
              onQueues={setOpenQueues}
              ageDays={openAgeDays}
              onAgeDays={setOpenAgeDays}
            />
          </div>
          <SurveyList csat={csat} range={csatRange} />

        </>
      )}

      {data.resources.length > 0 && (
        <div className="grid grid-3 mb-4">
          {data.resources.map((r) => {
            const body = (
              <>
                <span className="resource-icon" aria-hidden="true">
                  {r.Icon ?? '§'}
                </span>
                <span className="grow">
                  <span className="resource-name">{r.Name}</span>
                  {r.Description && <span className="resource-desc">{r.Description}</span>}
                </span>
                <span className="resource-open xs">Open →</span>
              </>
            );
            /* A leading slash means a module of this app, which must navigate
               in place; anything else is another system and opens in a tab. */
            return r.Url?.startsWith('/') ? (
              <Link key={r.ResourceId} className="resource-card" to={r.Url}>
                {body}
              </Link>
            ) : (
              <a
                key={r.ResourceId}
                className="resource-card"
                href={r.Url ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
              >
                {body}
              </a>
            );
          })}
        </div>
      )}

      {periods.length > 0 && (
        <section className="card">
          <div className="card-head">
            <div className="grow">
              <span className="eyebrow">History</span>
              <h2>Month by month</h2>
              <p className="sub">One reading is a number; the trend is the story.</p>
            </div>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th className="num">CSAT</th>
                  <th className="num">Billable</th>
                  <th className="num">Ack</th>
                  <th className="num">Response</th>
                  <th className="num">Resolution</th>
                  <th className="num">Opened</th>
                  <th className="num">Closed</th>
                  <th className="num">Kill ratio</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => (
                  <tr key={p.ScorecardId}>
                    <td>
                      <div className="strong">{p.PeriodLabel}</div>
                      <div className="xs muted mono">{formatDate(p.PeriodStart)}</div>
                    </td>
                    <td className="num">
                      {/* Same drill-down as the dial, per row: any month in
                          the history opens the responses behind its figure. */}
                      <Link to={csatLink(p)} title={`CSAT responses in ${p.PeriodLabel}`}>
                        {pct(p.CsatPercent)}
                      </Link>
                    </td>
                    <td className="num">{pct(p.BillablePercent)}</td>
                    <td className="num">{pct(p.SlaAcknowledgePercent)}</td>
                    <td className="num">{pct(p.SlaResponsePercent)}</td>
                    <td className="num">{pct(p.SlaResolutionPercent)}</td>
                    <td className="num">{p.TicketsOpened ?? '—'}</td>
                    <td className="num">{p.TicketsClosed ?? '—'}</td>
                    <td className="num">
                      {p.KillRatio === null ? (
                        '—'
                      ) : (
                        <span className={p.KillRatio < (t?.KillRatioTarget ?? 1) ? 'accent' : ''}>
                          {p.KillRatio.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="actions">
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setEditing(p)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
        </>
      )}

      {editing && (
        <PeriodEditor
          period={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            resource.reload();
          }}
          onError={setError}
        />
      )}

      {editingTargets && t && (
        <TargetEditor
          targets={t}
          onClose={() => setEditingTargets(false)}
          onSaved={() => {
            setEditingTargets(false);
            resource.reload();
          }}
        />
      )}
    </>
  );
}

const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(0)}%`);

/** `YYYY-MM-DD`, `days` after the given ISO date. UTC throughout: this is
 *  arithmetic on a calendar date, and a local-time round trip can move it. */
function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/**
 * The CSAT page, showing exactly the window this period covers.
 *
 * The window is half-open — `to` is the first instant NOT included — because
 * the alternative is deciding what a response submitted at 23:59 on the last
 * of the month belongs to, and half-open never has to. PeriodEnd is inclusive
 * when it is set at all, so it gets a day added; when it is not, a period is a
 * calendar month and the bound is the first of the next one.
 */
function csatLink(period: ServicePeriod): string {
  const from = period.PeriodStart.slice(0, 10);
  const [y, m] = from.split('-').map(Number) as [number, number];
  const to = period.PeriodEnd
    ? addDays(period.PeriodEnd, 1)
    : new Date(Date.UTC(y, m, 1)).toISOString().slice(0, 10);

  return `/csat?${new URLSearchParams({ from, to, label: period.PeriodLabel })}`;
}

/* Numeric fields are held as strings so an emptied box does not become 0
   under the cursor — same reason as the deal editor. */
const numOrNull = (s: string): number | null => {
  if (s.trim() === '') return null;
  const n = Number(s.replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : null;
};

function PeriodEditor({
  period,
  onClose,
  onSaved,
  onError,
}: {
  period: ServicePeriod | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const today = todayLocal();
  const firstOfMonth = `${today.slice(0, 7)}-01`;

  const [form, setForm] = useState({
    periodLabel:
      period?.PeriodLabel ??
      new Date().toLocaleDateString(undefined, { month: 'long', year: 'numeric' }),
    periodStart: period?.PeriodStart?.slice(0, 10) ?? firstOfMonth,
    csatPercent: period?.CsatPercent?.toString() ?? '',
    billablePercent: period?.BillablePercent?.toString() ?? '',
    slaAcknowledgePercent: period?.SlaAcknowledgePercent?.toString() ?? '',
    slaResponsePercent: period?.SlaResponsePercent?.toString() ?? '',
    slaResolutionPercent: period?.SlaResolutionPercent?.toString() ?? '',
    ticketsOpened: period?.TicketsOpened?.toString() ?? '',
    ticketsClosed: period?.TicketsClosed?.toString() ?? '',
    notes: period?.Notes ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const opened = numOrNull(form.ticketsOpened);
  const closed = numOrNull(form.ticketsClosed);
  const ratio = opened && opened > 0 && closed !== null ? closed / opened : null;

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.post('/service/periods', {
        periodLabel: form.periodLabel.trim(),
        periodStart: form.periodStart,
        csatPercent: numOrNull(form.csatPercent),
        billablePercent: numOrNull(form.billablePercent),
        slaAcknowledgePercent: numOrNull(form.slaAcknowledgePercent),
        slaResponsePercent: numOrNull(form.slaResponsePercent),
        slaResolutionPercent: numOrNull(form.slaResolutionPercent),
        ticketsOpened: opened,
        ticketsClosed: closed,
        notes: form.notes.trim() || null,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={period ? period.PeriodLabel : 'Record a month'}
      onClose={onClose}
      footer={
        <>
          {period && (
            <DeleteButton
              what={`the ${period.PeriodLabel} numbers`}
              onConfirm={() =>
                void api
                  .del(`/service/periods/${period.ScorecardId}`)
                  .then(onSaved)
                  .catch((err: unknown) =>
                    onError(err instanceof Error ? err.message : 'Could not delete'),
                  )
              }
            />
          )}
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !form.periodLabel.trim() || !form.periodStart}
            onClick={() => void submit()}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <div className="form-grid">
        <Field label="Period">
          <input
            className="input"
            value={form.periodLabel}
            onChange={(e) => set({ periodLabel: e.target.value })}
          />
        </Field>
        <Field label="Month starting" hint="Re-entering a month corrects it rather than duplicating it.">
          <input
            className="input"
            type="date"
            value={form.periodStart}
            onChange={(e) => set({ periodStart: e.target.value })}
          />
        </Field>
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <span className="eyebrow">Quality and time</span>
        <div className="form-grid mt-4">
          <Field label="CSAT %" hint="Positive responses on closure">
            <input
              className="input"
              inputMode="decimal"
              value={form.csatPercent}
              onChange={(e) => set({ csatPercent: e.target.value })}
              placeholder="—"
            />
          </Field>
          <Field label="Billable %" hint="Technician time sold">
            <input
              className="input"
              inputMode="decimal"
              value={form.billablePercent}
              onChange={(e) => set({ billablePercent: e.target.value })}
              placeholder="—"
            />
          </Field>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <span className="eyebrow">SLA attainment</span>
        <p className="xs muted mt-2">
          The share of tickets that met each clock — not the average time itself. A mean response
          time hides the handful that waited two days.
        </p>
        <div className="form-grid mt-4">
          <Field label="Time to acknowledge %">
            <input
              className="input"
              inputMode="decimal"
              value={form.slaAcknowledgePercent}
              onChange={(e) => set({ slaAcknowledgePercent: e.target.value })}
              placeholder="—"
            />
          </Field>
          <Field label="Time to response %">
            <input
              className="input"
              inputMode="decimal"
              value={form.slaResponsePercent}
              onChange={(e) => set({ slaResponsePercent: e.target.value })}
              placeholder="—"
            />
          </Field>
          <Field label="Time to resolution %">
            <input
              className="input"
              inputMode="decimal"
              value={form.slaResolutionPercent}
              onChange={(e) => set({ slaResolutionPercent: e.target.value })}
              placeholder="—"
            />
          </Field>
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 'var(--space-4)' }}>
        <span className="eyebrow">Kill ratio</span>
        <div className="form-grid mt-4">
          <Field label="Tickets opened">
            <input
              className="input"
              inputMode="numeric"
              value={form.ticketsOpened}
              onChange={(e) => set({ ticketsOpened: e.target.value })}
              placeholder="—"
            />
          </Field>
          <Field label="Tickets closed">
            <input
              className="input"
              inputMode="numeric"
              value={form.ticketsClosed}
              onChange={(e) => set({ ticketsClosed: e.target.value })}
              placeholder="—"
            />
          </Field>
        </div>
        <p className="small mt-4">
          {ratio === null ? (
            <span className="muted">Closed ÷ opened. Enter both to see the ratio.</span>
          ) : (
            <>
              <span className="mono strong">{ratio.toFixed(2)}</span>{' '}
              <span className="muted">
                {ratio >= 1 ? '— closing more than arrives' : '— the backlog is growing'}
              </span>
            </>
          )}
        </p>
      </div>

      <Field label="Notes">
        <textarea
          className="textarea"
          rows={2}
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
          placeholder="Anything that explains the month — an outage, a big project, a holiday period."
        />
      </Field>
    </Modal>
  );
}

function TargetEditor({
  targets,
  onClose,
  onSaved,
}: {
  targets: NonNullable<ServiceDelivery['targets']>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    csatTarget: String(targets.CsatTarget),
    billableTarget: String(targets.BillableTarget),
    slaAcknowledgeTarget: String(targets.SlaAcknowledgeTarget),
    slaResponseTarget: String(targets.SlaResponseTarget),
    slaResolutionTarget: String(targets.SlaResolutionTarget),
    killRatioTarget: String(targets.KillRatioTarget),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.put('/service/targets', {
        csatTarget: numOrNull(form.csatTarget) ?? undefined,
        billableTarget: numOrNull(form.billableTarget) ?? undefined,
        slaAcknowledgeTarget: numOrNull(form.slaAcknowledgeTarget) ?? undefined,
        slaResponseTarget: numOrNull(form.slaResponseTarget) ?? undefined,
        slaResolutionTarget: numOrNull(form.slaResolutionTarget) ?? undefined,
        killRatioTarget: numOrNull(form.killRatioTarget) ?? undefined,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save');
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Targets"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}

      <p className="xs muted">
        These default to the figures the OML improvement plan uses for a business at level 3 to 4,
        so the gauges and the maturity plan set the same bar rather than two different ones.
      </p>

      <div className="form-grid">
        <Field label="CSAT %">
          <input className="input" inputMode="decimal" value={form.csatTarget} onChange={(e) => set({ csatTarget: e.target.value })} />
        </Field>
        <Field label="Billable %" hint="75-85% is the usual band">
          <input className="input" inputMode="decimal" value={form.billableTarget} onChange={(e) => set({ billableTarget: e.target.value })} />
        </Field>
      </div>

      <div className="form-grid">
        <Field label="Acknowledge %">
          <input className="input" inputMode="decimal" value={form.slaAcknowledgeTarget} onChange={(e) => set({ slaAcknowledgeTarget: e.target.value })} />
        </Field>
        <Field label="Response %">
          <input className="input" inputMode="decimal" value={form.slaResponseTarget} onChange={(e) => set({ slaResponseTarget: e.target.value })} />
        </Field>
        <Field label="Resolution %">
          <input className="input" inputMode="decimal" value={form.slaResolutionTarget} onChange={(e) => set({ slaResolutionTarget: e.target.value })} />
        </Field>
      </div>

      <Field label="Kill ratio" hint="1.0 means closing exactly what arrives. Above 1.0 eats the backlog.">
        <input className="input" inputMode="decimal" value={form.killRatioTarget} onChange={(e) => set({ killRatioTarget: e.target.value })} />
      </Field>
    </Modal>
  );
}

interface KillRatioToday {
  opened: number;
  closed: number;
  ratio: number | null;
  fetchedUtc: string;
}

/**
 * When a dial's reading was taken. Each dial shows its last reading until its
 * own Refresh, however old — so one from an earlier day says which day, rather
 * than passing for today's.
 */
function readAt(iso: string) {
  const at = new Date(iso);
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  return at.toDateString() === new Date().toDateString()
    ? time
    : `${at.toLocaleDateString([], { day: 'numeric', month: 'short' })}, ${time}`;
}

/** The browser's own today, midnight to midnight, as UTC instants. */
function todayWindow() {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

/**
 * Today's kill ratio, read live from Autotask: tickets completed today over
 * tickets created today. Read once and kept: coming back to the page shows
 * what was read, and only Refresh asks Autotask again.
 *
 * Below the target the backlog grew today, so the hint says so in words —
 * the dial's colour is never the only signal.
 */
/**
 * Last week, Monday to Sunday, in this browser — the full week before the one
 * in progress, so it reads the same all week rather than shifting daily.
 */
function previousWeekWindow() {
  const monday = new Date();
  monday.setHours(0, 0, 0, 0);
  monday.setDate(monday.getDate() - ((monday.getDay() + 6) % 7));
  const from = new Date(monday);
  from.setDate(from.getDate() - 7);
  return { from: from.toISOString(), to: monday.toISOString() };
}

type KillPeriod = 'today' | 'previousWeek';
const killWindow = (period: KillPeriod) => (period === 'today' ? todayWindow() : previousWeekWindow());

/** "28 Sep – 4 Oct" for a window ending at the exclusive `to`. */
function weekLabel(from: string, to: string) {
  const last = new Date(to);
  last.setDate(last.getDate() - 1);
  const f = (d: Date) => d.toLocaleDateString([], { day: 'numeric', month: 'short' });
  return `${f(new Date(from))} – ${f(last)}`;
}

/* Read once and kept — coming back to the page does not ask Autotask again.
   Refresh does. Queues are in the path, so changing them is a different read
   rather than a stale one. */
function useKillRatio(period: KillPeriod, queues: number[], enabled = true) {
  const [{ from, to }] = useState(() => killWindow(period));
  const params = new URLSearchParams({ from, to, slot: period });
  if (queues.length) params.set('queues', [...queues].sort((a, b) => a - b).join(','));
  return { live: useHeldResource<KillRatioToday>(enabled ? `/service/kill-ratio?${params}` : null), from, to };
}

function LiveKillRatio({
  target,
  period = 'today',
  queues,
  queueOptions,
  onQueues,
}: {
  target: number;
  period?: KillPeriod;
  /** The queues counted — none means every queue. */
  queues: number[];
  /** Every Autotask queue, from the SLA read. Only the first dial shows the picker. */
  queueOptions?: { id: number; label: string }[];
  onQueues?: (q: number[]) => void;
}) {
  const { live, from, to } = useKillRatio(period, queues);
  const d = live.data;
  const when = period === 'today' ? 'today' : weekLabel(from, to);

  let hint: string;
  if (live.error) hint = live.error;
  else if (!d) hint = 'Reading Autotask…';
  else if (d.opened === 0) hint = `${d.closed} closed, none opened ${period === 'today' ? 'yet today' : when}`;
  else
    hint = `${d.closed} closed of ${d.opened} opened ${period === 'today' ? 'today' : when}${
      d.ratio !== null && d.ratio < target ? ' — backlog grew' : ''
    }`;

  return (
    <div className="live-kill">
      <Gauge
        label={period === 'today' ? 'Kill ratio — today' : 'Kill ratio — previous week'}
        value={d?.ratio ?? null}
        target={target}
        max={2}
        unit=""
        decimals={2}
        hint={hint}
      />
      {/* Under the first dial only, and it governs both: the two dials are the
          same measure over two windows, so letting them count different
          queues would make the pair unreadable. */}
      {onQueues && (
        <SlaQueuePicker options={queueOptions ?? []} value={queues} onChange={onQueues} />
      )}
      <div className="xs subtle live-kill-foot">
        {d ? `Live from Autotask · ${readAt(d.fetchedUtc)}` : 'Live from Autotask'}
        {' · '}
        <button
          type="button"
          className="link-button"
          disabled={live.refreshing}
          onClick={() => live.refresh()}
        >
          {live.refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}


interface SlaEvent {
  met: number;
  counted: number;
  percent: number | null;
}
interface SlaData {
  closed: number;
  withSla: number;
  acknowledge: SlaEvent;
  response: SlaEvent;
  resolution: SlaEvent;
  reopen: {
    resolved: number;
    reopened: number;
    percent: number | null;
    /** The reopened tickets themselves. Absent on a reading kept from before
     *  the dial could drill — one Refresh brings them. */
    tickets?: ReopenedTicket[];
  };
  /** The queues counted — none means every queue. */
  queueIds: number[];
  /** Every Autotask queue, for the picker. */
  queues: { id: number; label: string }[];
  fetchedUtc: string;
}

interface ReopenedTicket {
  id: number;
  ticketNumber: string;
  title: string;
  company: string | null;
  assignedTo: string | null;
  status: string;
  queue: string;
  firstResolved: string | null;
  /** Null while it is open again. */
  lastCompleted: string | null;
  reason: string;
}

/** SLA and reopen figures for the chosen range and queues, read once per choice and kept until Refresh. */
function useSla(range: CsatRange, queues: number[], enabled = true) {
  const { from, to } = useMemo(() => csatWindow(range), [range]);
  const params = new URLSearchParams({ from, to, slot: range });
  if (queues.length) params.set('queues', [...queues].sort((a, b) => a - b).join(','));
  return useHeldResource<SlaData>(enabled ? `/service/sla?${params}` : null);
}

/* Which queues the SLA dials count, remembered in this browser. Storage can be
   unavailable (a private window, blocked site data), so every read and write
   is guarded and the dials simply fall back to every queue. */
const SLA_QUEUES_KEY = 'service-delivery-sla-queues';
/* The kill ratio's own choice, under its own key for the same reasons. */
const KILL_QUEUES_KEY = 'service-delivery-kill-queues';
const CSAT_QUEUES_KEY = 'service-delivery-csat-queues';
const OPEN_QUEUES_KEY = 'service-delivery-open-queues';
const OPEN_AGE_DAYS_KEY = 'service-delivery-open-age-days';
/* Where the age dial opens, and the longest cut the control will take — a
   year of backlog is already every ticket the read returns. */
const OPEN_AGE_DEFAULT = 3;
const OPEN_AGE_MAX = 365;
function useKillQueues(): [number[], (q: number[]) => void] {
  return useStoredQueues(KILL_QUEUES_KEY);
}
function useCsatQueues(): [number[], (q: number[]) => void] {
  return useStoredQueues(CSAT_QUEUES_KEY);
}
function useOpenQueues(): [number[], (q: number[]) => void] {
  return useStoredQueues(OPEN_QUEUES_KEY);
}
/**
 * The age dial's cut, in days, remembered between visits.
 *
 * Three is where it opens, because that is the bar the scorecard holds the
 * desk to; anything stored that is not a sane number of days is ignored
 * rather than trusted, since a bad value here would empty the dial.
 */
function useOpenAgeDays(): [number, (d: number) => void] {
  const [days, setDays] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem(OPEN_AGE_DAYS_KEY));
      return Number.isFinite(v) && v >= 1 && v <= OPEN_AGE_MAX ? Math.round(v) : OPEN_AGE_DEFAULT;
    } catch {
      return OPEN_AGE_DEFAULT;
    }
  });
  const save = (d: number) => {
    setDays(d);
    try {
      localStorage.setItem(OPEN_AGE_DAYS_KEY, String(d));
    } catch {
      /* Not remembered; still applied for this visit. */
    }
  };
  return [days, save];
}
function useSlaQueues(): [number[], (q: number[]) => void] {
  return useStoredQueues(SLA_QUEUES_KEY);
}
function useStoredQueues(key: string): [number[], (q: number[]) => void] {
  const [queues, setQueues] = useState<number[]>(() => {
    try {
      const v: unknown = JSON.parse(localStorage.getItem(key) ?? '[]');
      return Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number') : [];
    } catch {
      return [];
    }
  });
  const save = (q: number[]) => {
    setQueues(q);
    try {
      localStorage.setItem(key, JSON.stringify(q));
    } catch {
      /* Not remembered; still applied for this visit. */
    }
  };
  return [queues, save];
}

/**
 * The queue picker under the first SLA dial. Ticks are a draft until Apply,
 * because each change is a fresh read of Autotask.
 */
function SlaQueuePicker({
  options,
  value,
  onChange,
}: {
  options: { id: number; label: string }[];
  value: number[];
  onChange: (q: number[]) => void;
}) {
  const [draft, setDraft] = useState<Set<number>>(() => new Set(value));
  const [open, setOpen] = useState(false);
  const names = value.map((id) => options.find((o) => o.id === id)?.label ?? `Queue ${id}`);
  const summary = value.length === 0 ? 'All queues' : value.length <= 2 ? names.join(', ') : `${value.length} queues`;
  const same = draft.size === value.length && value.every((id) => draft.has(id));
  return (
    <details
      className="sla-queues"
      open={open}
      onToggle={(e) => {
        const isOpen = (e.currentTarget as HTMLDetailsElement).open;
        setOpen(isOpen);
        if (isOpen) setDraft(new Set(value));
      }}
    >
      <summary className="xs" title={value.length ? names.join(', ') : 'Every queue counts'}>
        Queues: {summary}
      </summary>
      <div className="sla-queues-panel">
        {options.length === 0 ? (
          <p className="xs muted">Reading Autotask's queues…</p>
        ) : (
          <div className="sla-queues-list">
            {options.map((o) => (
              <label key={o.id} className="checkbox xs">
                <input
                  type="checkbox"
                  checked={draft.has(o.id)}
                  onChange={(e) => {
                    const next = new Set(draft);
                    if (e.target.checked) next.add(o.id);
                    else next.delete(o.id);
                    setDraft(next);
                  }}
                />
                {o.label}
              </label>
            ))}
          </div>
        )}
        <div className="row" style={{ gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-ghost btn-sm" disabled={draft.size === 0} onClick={() => setDraft(new Set())}>
            All queues
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={same}
            onClick={() => {
              onChange([...draft]);
              setOpen(false);
            }}
          >
            Apply
          </button>
        </div>
      </div>
    </details>
  );
}

/**
 * The three SLA dials and the reopen rate, live from Autotask: one cell each
 * in the gauge grid. The period control sits under the first dial and moves
 * all four. Acknowledge, response and resolution are Autotask's First
 * Response, Resolution Plan and Resolved events.
 */
function LiveSla({
  sla,
  range,
  onRange,
  queues,
  onQueues,
  targets,
}: {
  sla: ReturnType<typeof useSla>;
  range: CsatRange;
  onRange: (r: CsatRange) => void;
  queues: number[];
  onQueues: (q: number[]) => void;
  targets: { SlaAcknowledgeTarget: number; SlaResponseTarget: number; SlaResolutionTarget: number } | null;
}) {
  const d = sla.data;
  /* The queue list arrives with the figures; keep the last one while a new choice is read. */
  const [queueOptions, setQueueOptions] = useState<{ id: number; label: string }[]>([]);
  useEffect(() => {
    if (d?.queues?.length) setQueueOptions(d.queues);
  }, [d]);
  const words = queues.length ? `${CSAT_RANGE_WORDS[range]} in ${queues.length === 1 ? 'this queue' : 'these queues'}` : CSAT_RANGE_WORDS[range];
  const hintFor = (e: SlaEvent | undefined, what: string) => {
    if (sla.error) return sla.error;
    if (!d || !e) return 'Reading Autotask…';
    if (e.counted === 0) return `No closed tickets with a ${what} SLA ${words}`;
    return `${e.met} of ${e.counted} met · ${what} ${words}`;
  };
  const foot = (
    <div className="xs subtle live-kill-foot">
      {d
        ? `Autotask SLAs · ${readAt(d.fetchedUtc)}`
        : 'Autotask SLAs'}
      {' · '}
      <button type="button" className="link-button" disabled={sla.refreshing} onClick={() => sla.refresh()}>
        {sla.refreshing ? 'Refreshing…' : 'Refresh'}
      </button>
    </div>
  );
  const reopen = d?.reopen;
  const [drill, setDrill] = useState(false);
  const reopenedTickets = reopen?.tickets ?? [];
  const when = (iso: string | null) =>
    iso ? new Date(/(Z|[+-]\d\d:\d\d)$/.test(iso) ? iso : `${iso}Z`).toLocaleDateString([], { day: 'numeric', month: 'short' }) : '—';
  return (
    <>
      <div className="live-kill">
        <Gauge
          label="SLA — time to acknowledge"
          value={d?.acknowledge.percent ?? null}
          target={targets?.SlaAcknowledgeTarget ?? null}
          decimals={1}
          hint={hintFor(d?.acknowledge, 'first response')}
        />
        <div className="live-csat-range">
          <Segmented label="SLA period" options={CSAT_RANGES} value={range} onChange={onRange} />
        </div>
        <SlaQueuePicker options={queueOptions} value={queues} onChange={onQueues} />
        {foot}
      </div>
      <div className="live-kill">
        <Gauge
          label="SLA — time to response"
          value={d?.response.percent ?? null}
          target={targets?.SlaResponseTarget ?? null}
          decimals={1}
          hint={hintFor(d?.response, 'resolution plan')}
        />
        {foot}
      </div>
      <div className="live-kill">
        <Gauge
          label="SLA — time to resolution"
          value={d?.resolution.percent ?? null}
          target={targets?.SlaResolutionTarget ?? null}
          decimals={1}
          hint={hintFor(d?.resolution, 'resolved')}
        />
        {foot}
      </div>
      <div className="live-kill">
        {/* Lower is better, so no target mark: the dial's colours read higher as better. */}
        <Gauge
          label="Ticket reopen rate"
          value={reopen?.percent ?? null}
          target={null}
          max={25}
          decimals={1}
          onClick={reopenedTickets.length ? () => setDrill(true) : undefined}
          drillLabel="See the tickets"
          hint={
            sla.error
              ? sla.error
              : !reopen
                ? 'Reading Autotask…'
                : reopen.resolved === 0
                  ? `No tickets resolved ${words}`
                  : `${reopen.reopened} of ${reopen.resolved} resolved ${words} were reopened${
                      reopen.reopened && !reopen.tickets ? ' · Refresh to list them' : ''
                    }`
          }
        />
        <div className="xs subtle live-kill-foot" title="Reopened: not Complete now, or completed again more than an hour after Autotask first marked it resolved.">
          Autotask · first resolved vs. last completed
          {d && ` · ${readAt(d.fetchedUtc)}`}
          {' · '}
          <button type="button" className="link-button" disabled={sla.refreshing} onClick={() => sla.refresh()}>
            {sla.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <Drawer
        open={drill}
        onClose={() => setDrill(false)}
        wide
        title="Reopened tickets"
        subtitle={`${reopenedTickets.length} of ${reopen?.resolved ?? 0} resolved ${words} · first resolved, newest first`}
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Company</th>
                <th>Assigned</th>
                <th>Status</th>
                <th>Why</th>
                <th className="num">First resolved</th>
                <th className="num">Completed</th>
              </tr>
            </thead>
            <tbody>
              {reopenedTickets.map((t) => (
                <tr key={t.id}>
                  <td>
                    <span className="mono xs">{t.ticketNumber}</span>
                    <div className="xs muted">{t.title}</div>
                  </td>
                  <td>{t.company ?? '—'}</td>
                  <td>{t.assignedTo ?? <span className="subtle">Unassigned</span>}</td>
                  <td>
                    {t.status}
                    {t.queue && <div className="xs muted">{t.queue}</div>}
                  </td>
                  <td>{t.reason}</td>
                  <td className="num">{when(t.firstResolved)}</td>
                  <td className="num">{when(t.lastCompleted)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Drawer>
    </>
  );
}

type CsatRange = 'today' | 'week' | 'month' | 'year';

const CSAT_RANGES: { id: CsatRange; label: string }[] = [
  { id: 'today', label: 'Today' },
  { id: 'week', label: 'Last week' },
  { id: 'month', label: 'Last month' },
  { id: 'year', label: 'Last year' },
];

const CSAT_RANGE_WORDS: Record<CsatRange, string> = {
  today: 'today',
  week: 'in the last 7 days',
  month: 'in the last 30 days',
  year: 'in the last 12 months',
};

/** The window for a range, ending at the end of today in this browser. */
function csatWindow(range: CsatRange) {
  const { to } = todayWindow();
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  if (range === 'week') start.setDate(start.getDate() - 6);
  if (range === 'month') start.setDate(start.getDate() - 29);
  if (range === 'year') {
    start.setFullYear(start.getFullYear() - 1);
    start.setDate(start.getDate() + 1);
  }
  return { from: start.toISOString(), to };
}

interface CsatData {
  scaleMax: number;
  count: number;
  /** The queues counted — none means every queue. */
  queueIds: number[];
  /** Every Autotask queue, for the picker. */
  queues: { id: number; label: string }[];
  average: number | null;
  contactAverage: number | null;
  resourceAverage: number | null;
  responses: {
    id: number;
    completed: string | null;
    ticketNumber: string | null;
    ticketTitle: string | null;
    company: string | null;
    surveyRating: number | null;
    contactRating: number | null;
    resourceRating: number | null;
  }[];
  fetchedUtc: string;
}

/** CSAT for the chosen range, read once per range and kept until Refresh. */
function useCsat(range: CsatRange, queues: number[], enabled = true) {
  const { from, to } = useMemo(() => csatWindow(range), [range]);
  const params = new URLSearchParams({ from, to, slot: range });
  if (queues.length) params.set('queues', [...queues].sort((a, b) => a - b).join(','));
  return useHeldResource<CsatData>(enabled ? `/service/csat?${params}` : null);
}

type CsatResource = ReturnType<typeof useCsat>;

const rating = (v: number | null) => (v === null ? '—' : v.toFixed(2));

/**
 * CSAT from Autotask's built-in ticket survey: the average survey rating out
 * of 5 for the chosen period. The target mark is the CSAT target read on the
 * same 1–5 scale (90% of 5 is 4.50).
 */
function LiveCsat({
  csat,
  range,
  onRange,
  queues,
  onQueues,
  target,
}: {
  csat: CsatResource;
  range: CsatRange;
  onRange: (r: CsatRange) => void;
  queues: number[];
  onQueues: (q: number[]) => void;
  target: number | null;
}) {
  const d = csat.data;
  let hint: string;
  if (csat.error) hint = csat.error;
  else if (!d) hint = 'Reading Autotask…';
  else if (d.count === 0) hint = `No surveys returned ${CSAT_RANGE_WORDS[range]}`;
  else hint = `average of ${d.count} survey${d.count === 1 ? '' : 's'} ${CSAT_RANGE_WORDS[range]}`;

  return (
    <div className="live-kill">
      <Gauge
        label={`CSAT — ${CSAT_RANGES.find((r) => r.id === range)?.label.toLowerCase()}`}
        value={d?.average ?? null}
        target={target === null ? null : Math.round((target / 100) * 5 * 100) / 100}
        max={5}
        unit=""
        decimals={2}
        hint={hint}
      />
      <div className="live-csat-range">
        <Segmented label="CSAT period" options={CSAT_RANGES} value={range} onChange={onRange} />
      </div>
      <SlaQueuePicker options={csat.data?.queues ?? []} value={queues} onChange={onQueues} />
      <div className="xs subtle live-kill-foot">
        {d
          ? `Autotask survey, out of 5 · ${readAt(d.fetchedUtc)}`
          : 'Autotask survey, out of 5'}
        {' · '}
        <button type="button" className="link-button" disabled={csat.refreshing} onClick={csat.refresh}>
          {csat.refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}

/** The survey responses behind the dial, newest first. */
function SurveyList({ csat, range }: { csat: CsatResource; range: CsatRange }) {
  const d = csat.data;
  if (!d || d.count === 0) return null;
  const when = (iso: string | null) => {
    if (!iso) return '—';
    /* Autotask times are UTC; some come back without the zone marker. */
    const at = new Date(/(Z|[+-]\d\d:\d\d)$/.test(iso) ? iso : `${iso}Z`);
    return range === 'today'
      ? at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
      : at.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };
  return (
    <section className="card mb-4">
      <div className="card-head">
        <div className="grow">
          <h3>Surveys {CSAT_RANGE_WORDS[range]}</h3>
          <p className="sub">
            {d.count} returned · average {rating(d.average)} of 5 · contact {rating(d.contactAverage)} · resource{' '}
            {rating(d.resourceAverage)}
            {d.count > d.responses.length ? ` · newest ${d.responses.length} listed` : ''}
          </p>
        </div>
      </div>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>{range === 'today' ? 'Time' : 'Date'}</th>
              <th>Ticket</th>
              <th>Company</th>
              <th className="num">Survey</th>
              <th className="num">Contact</th>
              <th className="num">Resource</th>
            </tr>
          </thead>
          <tbody>
            {d.responses.map((x) => (
              <tr key={x.id}>
                <td className="xs subtle">{when(x.completed)}</td>
                <td>
                  <span className="mono xs">{x.ticketNumber ?? '—'}</span>
                  {x.ticketTitle && <div className="xs muted">{x.ticketTitle}</div>}
                </td>
                <td>{x.company ?? '—'}</td>
                <td className="num">{x.surveyRating ?? '—'}</td>
                <td className="num">{x.contactRating ?? '—'}</td>
                <td className="num">{x.resourceRating ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

interface OpenTicket {
  id: number;
  ticketNumber: string;
  title: string;
  company: string | null;
  assignedTo: string | null;
  status: string;
  priority: string;
  queue: string;
  created: string | null;
  ageDays: number;
  hoursWorked: number;
}

interface OpenTickets {
  openCount: number;
  hoursThreshold: number;
  /** The oldest cut this read serves: anything older than this is in overAge. */
  ageFloorDays: number;
  overHours: OpenTicket[];
  overAge: OpenTicket[];
  /** The queues counted — none means every queue. */
  queueIds: number[];
  /** Every Autotask queue, for the picker. */
  queues: { id: number; label: string }[];
  fetchedUtc: string;
}

/**
 * The open queue, live from Autotask: how many open tickets have had 2.5 hours
 * or more worked on them, and how many have been open longer than the desk's
 * chosen number of days. Each dial is out of every open ticket, and opens the
 * list behind it.
 *
 * The age cut is the reader's to set. Unlike the queue choice it is not in the
 * path: the read already returns everything open longer than its floor, so a
 * new number re-counts what is in hand and the dial moves as it is typed.
 */
function OpenTicketHealth({
  queues,
  onQueues,
  ageDays,
  onAgeDays,
}: {
  queues: number[];
  onQueues: (q: number[]) => void;
  ageDays: number;
  onAgeDays: (d: number) => void;
}) {
  const [drill, setDrill] = useState<'hours' | 'age' | null>(null);
  /* Read once and kept until Refresh. Queues are in the path, so narrowing
     is a different read rather than a stale one — and a narrowed read is a
     genuinely smaller one, since Autotask does the filtering. */
  const params = new URLSearchParams();
  if (queues.length) params.set('queues', [...queues].sort((a, b) => a - b).join(','));
  const live = useHeldResource<OpenTickets>(
    `/service/open-tickets${params.toString() ? `?${params}` : ''}`,
  );
  const d = live.data;

  const hint = (n: number) =>
    live.error
      ? live.error
      : !d
        ? 'Reading Autotask…'
        : `of ${d.openCount} open ticket${d.openCount === 1 ? '' : 's'}${d.openCount ? ` · ${Math.round((n / d.openCount) * 100)}%` : ''}`;

  /* The dial, the drawer and the hint all read this one list, so what the
     number says and what opens behind it can never disagree. A cut below what
     the read covers would quietly under-count, so it is held at the floor. */
  const floor = d?.ageFloorDays ?? 1;
  const cut = Math.max(ageDays, floor);
  const overAge = d ? d.overAge.filter((t) => t.ageDays > cut) : [];

  const rows = d ? (drill === 'hours' ? d.overHours : drill === 'age' ? overAge : []) : [];

  return (
    <>
      {/* Two more dials in the page's row, beside CSAT and the kill ratios —
          not a row of their own. */}
      <div className="live-kill">
        <Gauge
          label={`${d?.hoursThreshold ?? 2.5}+ hours worked`}
          value={d ? d.overHours.length : null}
          target={null}
          max={Math.max(1, d?.openCount ?? 1)}
          unit=""
          hint={hint(d?.overHours.length ?? 0)}
          onClick={d && d.overHours.length ? () => setDrill('hours') : undefined}
          drillLabel="See the tickets"
        />
        {/* Under the first of the pair, governing both: they are two views of
            the same open queue, so letting them count different queues would
            make the pair unreadable. */}
        <SlaQueuePicker options={d?.queues ?? []} value={queues} onChange={onQueues} />
        <div className="xs subtle live-kill-foot">
          {d ? `Live from Autotask · ${readAt(d.fetchedUtc)}` : 'Live from Autotask'}
          {' · '}
          <button type="button" className="link-button" disabled={live.refreshing} onClick={() => live.refresh()}>
            {live.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>
      <div className="live-kill">
        <Gauge
          label={`Open more than ${cut} day${cut === 1 ? '' : 's'}`}
          value={d ? overAge.length : null}
          target={null}
          max={Math.max(1, d?.openCount ?? 1)}
          unit=""
          hint={hint(overAge.length)}
          onClick={overAge.length ? () => setDrill('age') : undefined}
          drillLabel="See the tickets"
        />
        {/* Under its own dial, because it governs only this one. */}
        <AgeDaysPicker value={ageDays} floor={floor} onChange={onAgeDays} />
        <div className="xs subtle live-kill-foot">
          {d ? `Live from Autotask · ${readAt(d.fetchedUtc)}` : 'Live from Autotask'}
          {' · '}
          <button type="button" className="link-button" disabled={live.refreshing} onClick={() => live.refresh()}>
            {live.refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <Drawer
        open={drill !== null}
        onClose={() => setDrill(null)}
        wide
        title={drill === 'hours' ? `Open tickets with ${d?.hoursThreshold ?? 2.5}+ hours worked` : `Open tickets older than ${cut} day${cut === 1 ? '' : 's'}`}
        subtitle={`${rows.length} ticket${rows.length === 1 ? '' : 's'} · ${drill === 'hours' ? 'most hours first' : 'oldest first'} · hours are every time entry on the ticket`}
      >
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Company</th>
                <th>Assigned</th>
                <th>Status</th>
                <th className="num">Hours</th>
                <th className="num">Age</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td>
                    <span className="mono xs">{t.ticketNumber}</span>
                    <div className="xs muted">{t.title}</div>
                  </td>
                  <td>{t.company ?? '—'}</td>
                  <td>{t.assignedTo ?? <span className="subtle">Unassigned</span>}</td>
                  <td>
                    {t.status}
                    {t.priority && <div className="xs muted">{t.priority}</div>}
                  </td>
                  <td className="num">{t.hoursWorked.toFixed(2)}</td>
                  <td className="num">{t.ageDays < 1 ? '<1 d' : `${Math.floor(t.ageDays)} d`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Drawer>
    </>
  );
}

/**
 * How old is old, under the age dial.
 *
 * Typed rather than picked from a list: three days is the bar the scorecard
 * sets, but the question asked of the backlog on any given morning is "what
 * has been sitting a week", and a fixed set of buttons would never hold the
 * number someone actually wants.
 *
 * The field keeps the half-typed value as text — a number input that re-parses
 * every keystroke turns "1" on the way to "14" into a one-day dial — and the
 * dial follows as soon as what is typed is a usable number. Anything outside
 * the range the read can answer is clamped when the field is left, so the
 * number on screen is always the number the dial counted.
 */
function AgeDaysPicker({
  value,
  floor,
  onChange,
}: {
  value: number;
  floor: number;
  onChange: (d: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  /* Follows the value when it is changed elsewhere — clamping on blur, say. */
  const [seen, setSeen] = useState(value);
  if (seen !== value) {
    setSeen(value);
    setDraft(String(value));
  }
  const clamp = (n: number) => Math.min(OPEN_AGE_MAX, Math.max(floor, Math.round(n)));
  return (
    <label className="open-age-days xs">
      Older than
      <input
        type="number"
        className="input"
        min={floor}
        max={OPEN_AGE_MAX}
        step={1}
        value={draft}
        aria-label="Count tickets open more than this many days"
        onChange={(e) => {
          setDraft(e.target.value);
          const n = Number(e.target.value);
          if (e.target.value.trim() && Number.isFinite(n) && n >= floor && n <= OPEN_AGE_MAX) {
            const d = Math.round(n);
            setSeen(d);
            onChange(d);
          }
        }}
        onBlur={() => {
          const n = Number(draft);
          const d = draft.trim() && Number.isFinite(n) ? clamp(n) : value;
          setDraft(String(d));
          setSeen(d);
          if (d !== value) onChange(d);
        }}
      />
      days
    </label>
  );
}

/* --- the billable dial ---------------------------------------------------- */

type BillableWeek = 'current' | 'last';

const BILLABLE_WEEKS: { id: BillableWeek; label: string }[] = [
  { id: 'current', label: 'This week' },
  { id: 'last', label: 'Last week' },
];

const BILLABLE_WEEK_WORDS: Record<BillableWeek, string> = {
  current: 'this week so far',
  last: 'last week',
};

interface BillablePerson {
  id: number;
  name: string;
  active: boolean;
  hours: number;
  billable: number;
  billablePct: number | null;
  /** False for somebody on Autotask's roster who logged nothing this week —
   *  a new starter, or a week off. Choosable all the same. */
  worked: boolean;
}

interface BillableData {
  from: string;
  to: string;
  /** False until the billable roles and work types have been chosen. */
  configured: boolean;
  /** The whole desk: the active technicians, the set the report's own
   *  headline covers. */
  desk: { hours: number; billable: number; billablePct: number | null; technicians: number };
  /** Everyone who could have logged time: the week's people plus Autotask's
   *  roster, so a new starter can be chosen before their first entry. */
  resources: BillablePerson[];
  roster: { count: number; readUtc: string; changes: { joined: string[]; left: string[] } | null; unavailable: string | null };
  fetchedUtc: string;
}

/* The week is Monday-start in this browser — weekRange is the one definition,
   shared with Reports › Billable Labor so the two cannot disagree about which
   entries fall in a week. */
function useBillable(week: BillableWeek, enabled = true) {
  const { from, to } = useMemo(() => weekRange(week), [week]);
  return useHeldResource<BillableData>(enabled ? `/service/billable?${new URLSearchParams({ from, to, slot: week })}` : null);
}

/**
 * The four live dials' figures as this page opens: CSAT today, billable this
 * week, the SLA clocks this month and the kill ratio today — each over the
 * queues (or engineer) this browser last chose on the page. For the home
 * dashboard's Service Delivery card. The reads are the page's own, by the same
 * paths, so the card and the page share what is held and cannot disagree.
 * `enabled` false asks Autotask for nothing.
 */
export function useServiceHeadlines(enabled: boolean) {
  const [csatQueues] = useCsatQueues();
  const [slaQueues] = useSlaQueues();
  const [killQueues] = useKillQueues();
  const [person] = useBillablePerson();
  const csat = useCsat('today', csatQueues, enabled);
  const sla = useSla('month', slaQueues, enabled);
  const billable = useBillable('current', enabled);
  const kill = useKillRatio('today', killQueues, enabled).live;
  const b = billable.data;
  return {
    /** Average survey rating out of 5, today. */
    csat: csat.data?.average ?? null,
    csatCount: csat.data?.count ?? null,
    billablePct: person === null ? (b?.desk.billablePct ?? null) : (b?.resources.find((r) => r.id === person)?.billablePct ?? null),
    sla: sla.data ?? null,
    killRatio: kill.data?.ratio ?? null,
    /** Something has come back, so the card has figures rather than a wait. */
    ready: Boolean(csat.data || sla.data || b || kill.data),
    error: csat.error ?? sla.error ?? billable.error ?? kill.error,
  };
}

/* Whose billable time the dial shows — null is the whole desk. Remembered in
   this browser like the queue choices, and for the same reason: a manager
   watching one engineer should not have to say so again every morning. */
const BILLABLE_PERSON_KEY = 'service-delivery-billable-person';

function useBillablePerson(): [number | null, (id: number | null) => void] {
  const [who, setWho] = useState<number | null>(() => {
    try {
      const v = Number(localStorage.getItem(BILLABLE_PERSON_KEY));
      return Number.isFinite(v) && v > 0 ? v : null;
    } catch {
      return null;
    }
  });
  const save = (id: number | null) => {
    setWho(id);
    try {
      if (id === null) localStorage.removeItem(BILLABLE_PERSON_KEY);
      else localStorage.setItem(BILLABLE_PERSON_KEY, String(id));
    } catch {
      /* Not remembered; still applied for this visit. */
    }
  };
  return [who, save];
}

/**
 * Billable share, live from Autotask: one engineer's or the whole desk's, for
 * this week or last.
 *
 * The figure is the Billable Labor report's — billable hours over hours
 * worked, where billable is decided by the work roles and work types chosen
 * on that report (api/src/lib/billableLabor.ts, which both now read). It
 * replaces a percentage typed into the monthly scorecard by hand off
 * Autotask's own billable hours: a different question, which is why this dial
 * and the report used to disagree on screen. The month that was recorded is
 * still in the trend table below.
 */
function LiveBillable({
  billable,
  week,
  onWeek,
  person,
  onPerson,
  target,
}: {
  billable: ReturnType<typeof useBillable>;
  week: BillableWeek;
  onWeek: (w: BillableWeek) => void;
  person: number | null;
  onPerson: (id: number | null) => void;
  target: number | null;
}) {
  const d = billable.data;
  /* The technicians come back with the figures, so the picker costs no extra
     call — but only those who logged time in the week do. Keep everyone seen
     this visit, or the person being watched vanishes from their own picker
     the moment they have a quiet week. */
  const [seen, setSeen] = useState<BillablePerson[]>([]);
  useEffect(() => {
    if (!d) return;
    setSeen((prev) => {
      const by = new Map(prev.map((p) => [p.id, p]));
      for (const r of d.resources) by.set(r.id, r);
      return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [d]);

  const mine = person === null ? null : (d?.resources.find((r) => r.id === person) ?? null);
  /* A chosen engineer with no time in the week is an honest empty dial, not a
     zero and not the desk's figure. */
  const value = person === null ? (d?.desk.billablePct ?? null) : (mine?.billablePct ?? null);
  const words = BILLABLE_WEEK_WORDS[week];
  const whose = person === null ? 'the desk' : (seen.find((p) => p.id === person)?.name ?? 'this engineer');

  const hint = () => {
    if (billable.error) return billable.error;
    if (!d) return 'Reading Autotask…';
    if (!d.configured) return 'No billable work roles or work types chosen yet — set them on Reports › Billable Labor.';
    /* On the roster but nothing logged — a new starter, or a week off. Said
       by name, because an empty dial with no explanation reads as a fault. */
    if (person !== null && (!mine || !mine.worked)) return `No time logged by ${whose} ${words}`;
    const hours = person === null ? d.desk.hours : (mine?.hours ?? 0);
    const billed = person === null ? d.desk.billable : (mine?.billable ?? 0);
    if (!hours) return `No time logged ${words}`;
    const who = person === null ? ` across ${d.desk.technicians} active technicians` : '';
    return `${billed.toFixed(2)}h billable of ${hours.toFixed(2)}h worked ${words}${who}`;
  };

  return (
    <div className="live-kill">
      <Gauge
        label={person === null ? 'Billable' : `Billable — ${whose}`}
        value={value}
        target={target}
        decimals={1}
        hint={hint()}
      />
      <div className="live-csat-range">
        <Segmented label="Billable week" options={BILLABLE_WEEKS} value={week} onChange={onWeek} />
      </div>
      <label className="live-billable-person xs">
        <span className="muted">Who</span>
        <select
          className="input"
          value={person === null ? '' : String(person)}
          onChange={(e) => onPerson(e.target.value === '' ? null : Number(e.target.value))}
        >
          <option value="">Everyone</option>
          {seen.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.active ? '' : ' (inactive)'}
            </option>
          ))}
        </select>
      </label>
      {/* A roster that did not come back means the picker holds only the
          people with time this week, so a new starter is missing from it.
          Said plainly, because an absent name otherwise looks like a
          decision somebody made. */}
      {d?.roster?.unavailable && <div className="xs muted live-kill-foot">Autotask's roster could not be read — new starters may be missing from this list.</div>}
      <div
        className="xs subtle live-kill-foot"
        title={`The same rule and the same entries as Reports › Billable Labor.${d?.roster ? ` ${d.roster.count} technicians on Autotask's roster.` : ''}`}
      >
        {d
          ? `Billable Labor rules · ${readAt(d.fetchedUtc)}`
          : 'Billable Labor rules'}
        {' · '}
        <button type="button" className="link-button" disabled={billable.refreshing} onClick={() => billable.refresh()}>
          {billable.refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
    </div>
  );
}
